import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight, ShieldCheck, Scale, BarChart3, Users, Eye, Lock,
  Printer, ChevronRight, ChevronDown,
} from "lucide-react";
import { submitPortfolioContact } from "@/lib/api";
import { TEAM } from "@/lib/teamData";
import { useSectionTracking } from "@/lib/useSectionTracking";
import { EditableContentProvider, E } from "@/components/EditableText";
import AlaCartePricing from "@/components/guardian/AlaCartePricing";
import { TeamBio } from "@/components/portfolio/TeamBio";
import { DashboardMock } from "@/components/portfolio/DashboardMock";
import { ContactSection } from "@/components/portfolio/ContactSection";

/* ============================================================================
 * Portfolio Compliance Program — Tailored Microsite
 * Audience: Sponsor banks, accelerators, VCs, family offices, venture studios.
 * Brand: CompliSun cream/sun palette · serif headlines · mono labels.
 * All CTAs route to the in-page contact form → SendGrid → Colin.
 * ========================================================================== */

const CREAM = "#FDFBF5";
const CREAM_DEEP = "#FBF4DC";
const INK = "#0F1E2E";
const SUN = "#F5B400";
const SUN_DEEP = "#C98F00";
const EMBER = "#FF7B1F";

/* Pricing tiers (manager-side platform & oversight fee, monthly) */
const TIERS = [
  { id: "launch",     label: "Launch",     min: 1,  max: 10,  monthly: 2500, annual: 30000 },
  { id: "growth",     label: "Growth",     min: 11, max: 25,  monthly: 5000, annual: 60000 },
  { id: "scale",      label: "Scale",      min: 26, max: 50,  monthly: 9000, annual: 108000 },
  { id: "enterprise", label: "Enterprise", min: 51, max: 9999, monthly: null, annual: null },
];
const PER_COMPANY_MONTHLY = 149;

/* À la carte module catalog used by the new pricing calculator.
 * Per-company base ($1,000/mo) includes any 2 modules. Each module beyond
 * that adds $250/mo. Names match the operating taxonomy the team uses
 * inside Guardian. */
const MODULE_CATALOG = [
  { key: "monitoring",  label: "Monitoring & testing" },
  { key: "onboarding",  label: "Client onboarding" },
  { key: "vendor",      label: "Vendor management" },
  { key: "training",    label: "Training" },
  { key: "policies",    label: "Policies & procedures" },
  { key: "agreements",  label: "Agreements / consents / disclosures" },
  { key: "complaints",  label: "Complaint management" },
  { key: "issues",      label: "Issue management" },
  { key: "marketing",   label: "Marketing review" },
  { key: "licensing",   label: "Licensing" },
];
const BASE_PER_COMPANY = 1000;          // includes any 2 modules
const INCLUDED_MODULES = 2;
const EXTRA_MODULE_PRICE = 250;         // each module beyond the included 2
const CONSULTING_HOURLY = 150;
const LEGAL_HOURLY = 300;
const HOTLINE_RETAINER = 750;           // per company / month
/* Volume discount on the platform portion only. Reframes "more companies =
 * lower effective rate" — manager savings scale with portfolio size. */
const VOLUME_TIERS = [
  { upTo: 5,   discount: 0.00,  label: "1–5 companies"   },
  { upTo: 15,  discount: 0.10,  label: "6–15 companies"  },
  { upTo: 30,  discount: 0.15,  label: "16–30 companies" },
  { upTo: 999, discount: 0.20,  label: "31+ companies"   },
];

/* TEAM roster shared with the core deck — see @/lib/teamData */

const TRACKED_SECTIONS = [
  "hero", "why", "dashboard", "whyus", "pricing", "closed", "team", "voices", "contact",
];


function tierForSize(n) {
  if (n <= 10) return TIERS[0];
  if (n <= 25) return TIERS[1];
  if (n <= 50) return TIERS[2];
  return TIERS[3];
}

export default function PortfolioProposal({ editMode = false }) {
  const [portfolioSize, setPortfolioSize] = useState(15);
  const [subsidy, setSubsidy] = useState("full"); // full | split | passthrough
  const { seenSections, engagementPct } = useSectionTracking(TRACKED_SECTIONS);

  const tier = tierForSize(portfolioSize);
  const platformMonthly = tier.monthly;
  const passthrough = subsidy === "passthrough";
  const split = subsidy === "split";
  const managerCompanyShare = passthrough ? 0 : (split ? 0.5 : 1);
  const companyShare = passthrough ? 1 : (split ? 0.5 : 0);
  const companyMembershipMonthly = portfolioSize * PER_COMPANY_MONTHLY;
  const managerMembershipPortion = Math.round(companyMembershipMonthly * managerCompanyShare);
  const perCompanyPaidByCompany = Math.round(PER_COMPANY_MONTHLY * companyShare);
  const totalManagerMonthly = (platformMonthly || 0) + managerMembershipPortion;
  const totalManagerAnnual = totalManagerMonthly * 12;

  const handlePrint = () => window.print();
  const jumpTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <EditableContentProvider ns="portfolio" editMode={editMode}>
    <div
      data-testid="portfolio-proposal"
      className="min-h-screen w-full antialiased text-ink-900"
      style={{ background: `linear-gradient(180deg, ${CREAM} 0%, ${CREAM_DEEP} 100%)`, color: INK }}
    >
      <style>{`
        .pcp-h { font-family: 'Source Serif Pro', 'Source Serif 4', Georgia, serif; font-weight: 300; letter-spacing: -0.02em; line-height: 1.05; }
        .pcp-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; letter-spacing: 0.18em; text-transform: uppercase; }
        .pcp-card { background: rgba(255,255,255,0.7); border: 1px solid rgba(15,30,46,0.08); border-radius: 14px; backdrop-filter: blur(6px); }
        .pcp-grain { background-image: radial-gradient(rgba(15,30,46,0.04) 1px, transparent 1px); background-size: 4px 4px; }
        .pcp-stripe { background: repeating-linear-gradient(135deg, rgba(245,180,0,0.10) 0, rgba(245,180,0,0.10) 12px, transparent 12px, transparent 24px); }
        @keyframes pcpBounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(6px); }
        }
        @media print { .no-print { display: none !important; } }
      `}</style>

      {/* Sticky header */}
      <header className="sticky top-0 z-40 backdrop-blur-md no-print" style={{ background: "rgba(253, 251, 245, 0.85)", borderBottom: "1px solid rgba(15,30,46,0.06)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <div className="text-base font-medium" style={{ color: INK }}>CompliSun</div>
              <div className="pcp-mono text-[9px]" style={{ color: SUN_DEEP }}>Portfolio Compliance Program</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => jumpTo("dashboard")} className="pcp-mono text-[10px] hover:opacity-100 opacity-60 transition" data-testid="nav-dashboard">Dashboard</button>
            <button onClick={() => jumpTo("whyus")} className="pcp-mono text-[10px] hover:opacity-100 opacity-60 transition" data-testid="nav-whyus">Why us</button>
            <button onClick={() => jumpTo("pricing")} className="pcp-mono text-[10px] hover:opacity-100 opacity-60 transition" data-testid="nav-pricing">Pricing</button>
            <button onClick={() => jumpTo("team")} className="pcp-mono text-[10px] hover:opacity-100 opacity-60 transition" data-testid="nav-team">Team</button>
            <button onClick={() => jumpTo("contact")} className="pcp-mono text-[10px] hover:opacity-100 opacity-60 transition" data-testid="nav-contact">Contact</button>
            <button
              onClick={() => jumpTo("contact")}
              className="pcp-mono text-[10px] px-4 py-2 rounded-md text-cream transition hover:translate-y-[-1px]"
              style={{ background: INK, color: CREAM }}
              data-testid="nav-cta"
            >Get in touch <ChevronRight className="inline h-3 w-3 ml-1" /></button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section id="hero" className="relative px-6 md:px-12 pt-14 md:pt-24 pb-20 md:pb-28 overflow-hidden">
        <div className="pointer-events-none absolute -top-32 -right-40 h-[600px] w-[600px] rounded-full blur-3xl opacity-50" style={{ background: `radial-gradient(circle, ${SUN} 0%, transparent 70%)` }} />
        <div className="pointer-events-none absolute -bottom-40 -left-32 h-[500px] w-[500px] rounded-full blur-3xl opacity-25" style={{ background: `radial-gradient(circle, ${EMBER} 0%, transparent 70%)` }} />
        <div className="relative max-w-6xl mx-auto">
          <h1 className="pcp-h text-5xl md:text-7xl lg:text-[88px] mb-8 max-w-5xl">
            <E k="hero.headline" defaultText="Compliance is your competitive advantage — whether you're one company or a whole portfolio." as="span" multiline />
          </h1>
          <p className="max-w-3xl text-lg md:text-xl leading-relaxed opacity-80 mb-10">
            <E
              k="hero.body"
              defaultText="CompliSun gives every company — whether it's yours alone or one of many you back — compliance automation, expert analysis, and hands-on support. Built by people who've run the function inside real banks and fintechs, not engineers guessing at what compliance needs."
              as="span"
              multiline
            />
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => jumpTo("contact")}
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-md font-medium transition hover:translate-y-[-1px]"
              style={{ background: INK, color: CREAM }}
              data-testid="hero-cta-primary"
            >
              Get in touch <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" />
            </button>
            <button
              onClick={() => jumpTo("pricing")}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-md font-medium border transition"
              style={{ borderColor: "rgba(15,30,46,0.15)", color: INK, background: "rgba(255,255,255,0.6)" }}
              data-testid="hero-cta-secondary"
            >
              See the pricing calculator
            </button>
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-5 py-4 rounded-md pcp-mono text-[10px] opacity-60 hover:opacity-100 transition no-print"
              data-testid="hero-print"
            >
              <Printer className="h-3.5 w-3.5" /> Print / save PDF
            </button>
          </div>

          {/* Trust strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl">
            {[
              { k: "One relationship", v: "Coverage that fits — one company or many" },
              { k: "Immediate automation", v: "Then iteration and improvement" },
              { k: "Detroit-rooted", v: "Attorney-led" },
              { k: "Predictable costs", v: "No surprise compliance bills" },
            ].map((t) => (
              <div key={t.k} className="pcp-card p-4">
                <div className="pcp-mono text-[9px]" style={{ color: SUN_DEEP }}>{t.k}</div>
                <div className="text-sm font-medium mt-1">{t.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll-down indicator — clickable, gently bouncing */}
        <button
          type="button"
          onClick={() => jumpTo("why")}
          data-testid="hero-scroll-down"
          aria-label="Scroll to next section"
          className="no-print absolute left-1/2 -translate-x-1/2 bottom-6 md:bottom-8 inline-flex flex-col items-center gap-1 opacity-65 hover:opacity-100 transition"
          style={{ color: INK }}
        >
          <span className="pcp-mono text-[9px]" style={{ letterSpacing: "0.24em", color: SUN_DEEP }}>SCROLL</span>
          <span
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border"
            style={{ borderColor: "rgba(15,30,46,0.20)", background: "rgba(255,255,255,0.55)", animation: "pcpBounce 2.2s ease-in-out infinite" }}
          >
            <ChevronDown className="h-4 w-4" strokeWidth={1.8} />
          </span>
        </button>
      </section>

      {/* WHY — section 2 · problem + 4 value props */}
      <section id="why" className="px-6 md:px-12 py-16 md:py-24" style={{ background: "rgba(255,255,255,0.4)", borderTop: "1px solid rgba(15,30,46,0.06)", borderBottom: "1px solid rgba(15,30,46,0.06)" }}>
        <div className="max-w-6xl mx-auto">
          <p className="pcp-mono text-[11px] mb-4" style={{ color: SUN_DEEP }}>/ 02 · The promise</p>
          <h2 className="pcp-h text-4xl md:text-5xl mb-6 max-w-4xl">
            <E k="why.headline" defaultText="Give every company the compliance edge to succeed — solo or across a whole portfolio." as="span" multiline />
          </h2>
          <p className="text-lg md:text-xl leading-relaxed opacity-85 max-w-3xl mb-5">
            <E
              k="why.body1"
              defaultText="Predictable costs so you always know how your money — or your fund's money — is being used, in a way that scales whether you run one company or back many. Every company gets the experts who do this work for a living, plus a system that scales with it as it grows."
              as="span"
              multiline
            />
          </p>
          <p className="text-base md:text-lg leading-relaxed opacity-80 max-w-3xl mb-10">
            <E
              k="why.body2"
              defaultText="Compliance becomes a clean story you can tell to LPs, regulators, and acquirers — with receipts, not vibes."
              as="span"
              multiline
            />
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { k: "01", t: "A guide for the uncertainty", b: "Someone who's done this before, telling you exactly what to tackle and in what order. Less guessing, faster decisions." },
              { k: "02", t: "Predictable costs", b: "Flat platform per company. Compliance hours on a known retainer or pool — no surprise bills, no scope creep." },
              { k: "03", t: "Tell your compliance story", b: "A clean record you can show to partners, regulators, and your board — proof, not vibes." },
              { k: "04", t: "Affordable, finally", b: "You always thought you couldn't afford a state-of-the-art system plus battle-tested experts. With CompliSun, you can." },
            ].map(({ k, t, b }, i) => (
              <div key={k} className="pcp-card p-5">
                <div className="pcp-mono text-[10px] mb-3" style={{ color: SUN_DEEP }}>/ {k}</div>
                <div className="text-base font-semibold mb-2" style={{ color: INK }}>
                  <E k={`why.point${i + 1}.title`} defaultText={t} as="span" />
                </div>
                <p className="text-sm opacity-75 leading-relaxed">
                  <E k={`why.point${i + 1}.body`} defaultText={b} as="span" multiline />
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DASHBOARD — moved up · section 3 in the new flow */}
      <DashboardMock />

      {/* WHY US — replaces the old ICPs + how-it-works sections */}
      <section id="whyus" className="px-6 md:px-12 py-16 md:py-24" style={{ background: "rgba(255,255,255,0.4)", borderTop: "1px solid rgba(15,30,46,0.06)", borderBottom: "1px solid rgba(15,30,46,0.06)" }}>
        <div className="max-w-6xl mx-auto">
          <p className="pcp-mono text-[11px] mb-4" style={{ color: SUN_DEEP }}>/ Why CompliSun</p>
          <h2 className="pcp-h text-4xl md:text-5xl mb-6 max-w-4xl">
            <E k="whyus.headline" defaultText="This isn't a tech team guessing at compliance. It's compliance professionals running the system." as="span" multiline />
          </h2>
          <p className="text-base md:text-lg opacity-85 max-w-3xl mb-5 leading-relaxed">
            <E
              k="whyus.body1"
              defaultText="Our team is embedded in each company, so they know the actual risk profile. They won't hesitate to flag something or bring in outside counsel — because they understand the real risk and reward, not a software vendor avoiding hard conversations."
              as="span"
              multiline
            />
          </p>
          <p className="text-base md:text-lg opacity-80 max-w-3xl mb-10 leading-relaxed">
            <E
              k="whyus.body2"
              defaultText="Every company gets the systems and expertise of a much larger, established operation — regardless of size. No surprise compliance gaps as it scales. You get regular Report Cards on each company's compliance health, so problems surface early — not during diligence or an exit."
              as="span"
              multiline
            />
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { Icon: Users,       t: "Embedded, not outsourced",       b: "Our compliance officers sit with each company. They learn the product, the regulators, and what's actually at stake." },
              { Icon: Scale,       t: "We call outside counsel when it matters", b: "We know our scope. If a situation needs a law firm or a litigator, we say so — and we manage the relationship for you." },
              { Icon: ShieldCheck, t: "Enterprise-grade compliance at any size", b: "Every company gets the controls, documentation, and posture of a much larger operation — without the headcount." },
              { Icon: BarChart3,   t: "Report cards, not surprises",     b: "Regular Report Cards on each company's compliance health. Problems surface early — not during diligence or an exit." },
            ].map(({ Icon, t, b }, i) => (
              <div key={t} className="pcp-card p-5">
                <Icon className="h-5 w-5 mb-3" style={{ color: SUN_DEEP }} strokeWidth={1.6} />
                <div className="text-base font-semibold mb-2" style={{ color: INK }}>
                  <E k={`whyus.card${i + 1}.t`} defaultText={t} as="span" />
                </div>
                <p className="text-sm opacity-75 leading-relaxed">
                  <E k={`whyus.card${i + 1}.b`} defaultText={b} as="span" multiline />
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING — section 5 · à la carte rebuild */}
      <AlaCartePricing />

      {/* CLOSED SYSTEM — Your data, your bucket, your gates */}
      <section id="closed" className="px-6 md:px-12 py-16 md:py-24" style={{ background: "rgba(255,255,255,0.4)", borderTop: "1px solid rgba(15,30,46,0.06)", borderBottom: "1px solid rgba(15,30,46,0.06)" }}>
        <div className="max-w-6xl mx-auto">
          <p className="pcp-mono text-[11px] mb-4" style={{ color: SUN_DEEP }}>/ Your data, your bucket, your gates</p>
          <h2 className="pcp-h text-4xl md:text-5xl mb-6 max-w-4xl">
            <E k="closed.headline" defaultText="Let your team use AI — without handing over your data." as="span" multiline />
          </h2>
          <p className="text-base md:text-lg leading-relaxed opacity-85 max-w-3xl mb-5">
            <E
              k="closed.lede1"
              defaultText="You want your team using AI. What you don't want is someone pasting proprietary work — or worse, client NPPI — into a tool nobody controls, with no rules and no record of where it went."
              as="span"
              multiline
            />
          </p>
          <p className="text-base md:text-lg leading-relaxed opacity-80 max-w-3xl mb-12">
            <E
              k="closed.lede2"
              defaultText={`Guardian lets your team use AI on your own data, inside your own environment. You write the rules. You build the prompts. The system is designed so your data stays in your walls — not retained by the AI, not used to train it, not routed anywhere you can't see. No black box, no "just trust us." That's the difference between privacy you're promised and privacy built into the design.`}
              as="span"
              multiline
            />
          </p>

          <p className="pcp-mono text-[11px] mb-4" style={{ color: SUN_DEEP }}>/ How it&apos;s wired</p>
          <h3 className="pcp-h text-3xl md:text-4xl mb-8 max-w-3xl">
            <E k="closed.subhead" defaultText="It runs in your house, not ours." as="span" multiline />
          </h3>

          <div className="grid md:grid-cols-2 gap-5">
            {[
              {
                t: "Everything in your AWS, walled off per client",
                b: "Your data lives inside your own AWS environment, and each client is walled off from every other. Encrypted with keys only you hold — at rest and in transit.",
                spec: "Per-client AWS VPC · customer-managed KMS keys · TLS 1.2+",
                Icon: Lock,
              },
              {
                t: "The AI comes to your data — your data isn't shipped off to the AI company",
                b: "When your team uses Claude, the request travels over a private AWS connection that never touches the open internet. Anthropic isn't provisioned to access your prompts, completions, or documents, and per AWS Bedrock terms, what you send isn't used to train models. Protected by design, not by a clause in a contract.",
                spec: "Claude via AWS Bedrock + VPC PrivateLink · isolated by architecture · no training use (per Bedrock terms)",
                Icon: ShieldCheck,
              },
              {
                t: "You decide who sees what",
                b: "Set the rules so one person sees training and policies, another sees NPPI, and no one sees more than their job needs. You set the gates.",
                spec: "Role-based access controls",
                Icon: Users,
              },
              {
                t: "Every move is recorded — in your account",
                b: "Every AI call leaves a log in your own CloudTrail. When an examiner asks who did what, you hand them a clean trail from your own systems — not a printout from a vendor's.",
                spec: "Audit logs in your CloudTrail · IAM · VPC isolation · GuardDuty monitoring",
                Icon: Eye,
              },
            ].map(({ t, b, spec, Icon }) => (
              <div key={t} className="pcp-card p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Icon className="h-5 w-5" style={{ color: SUN_DEEP }} strokeWidth={1.6} />
                </div>
                <div className="font-semibold mb-2 text-lg" style={{ color: INK }}>{t}</div>
                <p className="text-sm opacity-75 leading-relaxed mb-3">{b}</p>
                <div className="pcp-mono text-[10px] pt-3 border-t" style={{ color: SUN_DEEP, borderColor: "rgba(245,180,0,0.25)", letterSpacing: "0.14em" }}>{spec}</div>
              </div>
            ))}
          </div>

          <div className="pcp-card p-5 mt-8 pcp-stripe" style={{ borderLeft: `4px solid ${SUN}` }}>
            <div className="pcp-mono text-[10px] mb-2" style={{ color: SUN_DEEP }}>/ Protection by design · current best practices</div>
            <p className="text-sm md:text-base opacity-85">
              <E
                k="closed.footer"
                defaultText="Defensible, not just promised. We can walk your CISO, your sponsor bank's TPRM team, or an examiner through every layer."
                as="span"
                multiline
              />
            </p>
          </div>

          {/* Prompt governance — new */}
          <div className="mt-12">
            <p className="pcp-mono text-[11px] mb-3" style={{ color: SUN_DEEP }}>/ Governance · we approve the prompts</p>
            <h3 className="pcp-h text-2xl md:text-3xl mb-4 max-w-3xl">
              <E
                k="closed.governance.headline"
                defaultText="We approve the prompts. You don't have to guess what your team is asking AI."
                as="span"
                multiline
              />
            </h3>
            <p className="text-base opacity-80 max-w-3xl leading-relaxed">
              <E
                k="closed.governance.body"
                defaultText="CompliSun reviews and approves the prompts used inside Guardian, so teams aren't going rogue with unmanaged AI use. No one is freelancing with sensitive data; no one is pasting NPPI into an arbitrary chatbot. Every prompt is scoped, vetted, and tied back to a defensible workflow."
                as="span"
                multiline
              />
            </p>
          </div>

          {/* Legal notetaker callout — new privileged note-taking box */}
          <div className="mt-10 pcp-card p-6 md:p-7" style={{ background: INK, color: CREAM, borderColor: SUN, borderWidth: 2 }} data-testid="legal-notetaker-callout">
            <div className="flex items-center gap-2 pcp-mono text-[10px] mb-3" style={{ color: SUN, letterSpacing: "0.18em" }}>
              <Lock className="h-3.5 w-3.5" /> ATTORNEY-CLIENT PRIVILEGE · ATTORNEY WORK PRODUCT
            </div>
            <h3 className="pcp-h text-2xl md:text-3xl mb-3">
              <E
                k="closed.notetaker.headline"
                defaultText="The meeting notetaker that comes with privilege built in."
                as="span"
                multiline
              />
            </h3>
            <p className="text-base opacity-90 mb-3 leading-relaxed max-w-3xl">
              <E
                k="closed.notetaker.body"
                defaultText="CompliSun Legal's notetaker sits inside the legal engagement — so notes and analysis it generates from client meetings carry attorney-client privilege and attorney work product protections. Not the case with a generic AI notetaker tool, which produces a discoverable transcript with no legal protection."
                as="span"
                multiline
              />
            </p>
            <p className="text-sm opacity-75 max-w-3xl">
              The same conversation, transcribed by a free notetaker vs. by ours: one ends up in discovery, the other is protected work product. The architecture matters.
            </p>
          </div>
        </div>
      </section>

      {/* MEET THE TEAM — new section */}
      <section id="team" className="px-6 md:px-12 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <p className="pcp-mono text-[11px] mb-4" style={{ color: SUN_DEEP }}>/ Meet the team</p>
          <h2 className="pcp-h text-4xl md:text-5xl mb-6 max-w-4xl">
            <E k="team.headline" defaultText="Real compliance professionals. Not engineers learning compliance on your dime." as="span" multiline />
          </h2>
          <p className="text-base md:text-lg opacity-80 max-w-3xl mb-10 leading-relaxed">
            <E
              k="team.intro"
              defaultText="The people who'll actually work with your company — or across your whole portfolio — plus the founder behind the system."
              as="span"
              multiline
            />
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
            {TEAM.map((m, i) => (
              <div key={m.name} className="pcp-card p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-shrink-0 overflow-hidden rounded-full border-2" style={{ height: 56, width: 56, borderColor: `${SUN}55`, background: "rgba(15,30,46,0.04)" }}>
                    <img
                      src={m.photo}
                      alt={m.name}
                      className="w-full h-full"
                      style={{ objectFit: "cover", objectPosition: "center 20%" }}
                      loading="lazy"
                      data-testid={`team-photo-${i}`}
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold leading-tight" style={{ color: INK }}>
                      <E k={`team.m${i + 1}.name`} defaultText={m.name} as="span" />
                    </div>
                    <div className="pcp-mono text-[9px] mt-1" style={{ color: SUN_DEEP, letterSpacing: "0.14em" }}>
                      <E k={`team.m${i + 1}.role`} defaultText={m.role} as="span" />
                    </div>
                  </div>
                </div>
                <TeamBio i={i} text={m.bio} />
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* CLIENT VOICES — testimonials */}
      <section id="voices" className="px-6 md:px-12 py-16 md:py-24" style={{ background: "rgba(255,255,255,0.4)", borderTop: "1px solid rgba(15,30,46,0.06)", borderBottom: "1px solid rgba(15,30,46,0.06)" }}>
        <div className="max-w-6xl mx-auto">
          <p className="pcp-mono text-[11px] mb-4" style={{ color: SUN_DEEP }}>/ What our clients say</p>
          <h2 className="pcp-h text-4xl md:text-5xl mb-10 max-w-4xl">
            <E k="voices.headline" defaultText="What clients say when it's their company — or their whole portfolio — on the line." as="span" multiline />
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              {
                name: "Pia Thompson",
                title: "CEO · Checkbook",
                quote: "You need the right partner. We chose the right partner with CompliSun, and I honestly don't see bringing in a whole Compliance Department again. With CompliSun, the work gets done, no sacrifices, and far better quality than one would get from most fulltime hires.",
              },
              {
                name: "Mark Mughabghab",
                title: "CEO · RNPL Credit",
                quote: "Partnering with CompliSun Guardian was instrumental in strengthening our compliance management systems. It significantly reduced both cost and time when implementing marketing audits and securing our lending licenses.",
              },
            ].map((q, i) => (
              <blockquote key={q.name} className="pcp-card p-6 md:p-7 relative" data-testid={`voice-${i}`}>
                <div className="absolute top-4 right-5 pcp-h text-6xl leading-none opacity-15" style={{ color: SUN_DEEP }}>&ldquo;</div>
                <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: INK }}>
                  <E k={`voice.${i + 1}.quote`} defaultText={q.quote} as="span" multiline />
                </p>
                <footer className="pt-3 border-t" style={{ borderColor: "rgba(15,30,46,0.10)" }}>
                  <div className="text-sm font-semibold" style={{ color: INK }}>
                    <E k={`voice.${i + 1}.name`} defaultText={q.name} as="span" />
                  </div>
                  <div className="pcp-mono text-[10px]" style={{ color: SUN_DEEP, letterSpacing: "0.14em" }}>
                    <E k={`voice.${i + 1}.title`} defaultText={q.title} as="span" />
                  </div>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FORM — section 10 */}
      <ContactSection
        portfolioSize={portfolioSize}
        tier={tier}
        totalManagerMonthly={totalManagerMonthly}
        totalManagerAnnual={totalManagerAnnual}
        engagementPct={engagementPct}
        seenSections={Array.from(seenSections)}
        trackedTotal={TRACKED_SECTIONS.length}
      />

      {/* Footer */}
      <footer className="px-6 md:px-12 py-12 mt-8" style={{ borderTop: "1px solid rgba(15,30,46,0.08)" }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-7 w-7 object-contain" />
            <div className="leading-tight">
              <div className="text-sm font-medium">CompliSun</div>
              <div className="pcp-mono text-[9px] opacity-50">Detroit-rooted · Attorney-led</div>
            </div>
          </div>
          <div className="pcp-mono text-[10px] opacity-50">© {new Date().getFullYear()} CompliSun · Portfolio Compliance Program · Engagement {engagementPct}%</div>
        </div>
      </footer>
    </div>
    </EditableContentProvider>
  );
}
